import React from "react";
import { PartyPopper, Martini, Building2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Children's Events",
  description: "Creative, immersive children’s parties that spark curiosity, laughter, and wide-eyed wonder. Kids don’t just attend — they create, explore, and shine. We design the fun, manage the flow, and handle every detail so the magic feels effortless.",
  icon: PartyPopper
}, {
  title: "Adult Celebrations",
  description: "Not just a party — an experience. We design creative, joy-filled adult celebrations with intentional touches of magic woven into every detail. Guests don’t just attend — they connect, create, and celebrate, while we guide the flow so hosting feels effortless.",
  icon: Martini
}, {
  title: "Corporate & Nonprofit",
  description: "Fun, engaging events built around connection, creativity, and shared experience. We blend thoughtful coordination with meaningful, interactive touches so your gathering feels smooth, memorable, and beautifully put together.",
  icon: Building2
}, {
  title: "Weddings & Receptions",
  description: "Elegant and enchanting wedding experiences thoughtfully designed around your story. With intentional details and a touch of magic woven throughout, we ensure your celebration feels stress-free, meaningful, and truly unforgettable.",
  icon: Sparkles
}];
export default function MagicalExperiences() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="magical-experiences" ref={ref} className="relative w-full py-24 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-[10%] w-2 h-2 rounded-full bg-secondary/40 blur-[1px]" />
        <div className="absolute top-40 right-[15%] w-3 h-3 rounded-full bg-secondary/30 blur-[2px]" />
        <div className="absolute bottom-20 left-[20%] w-1.5 h-1.5 rounded-full bg-secondary/50 blur-[1px]" />
        <div className="absolute top-1/2 right-[5%] w-4 h-4 rounded-full bg-secondary/20 blur-[4px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`text-center max-w-5xl mx-auto pt-16 mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-5xl md:text-7xl font-medium text-primary mb-6">
            Experience the Magic
          </h2>
          <p className="text-xl leading-relaxed !text-[hsl(0,0%,100%)] !italic md:text-2xl">Abracadabra Events delivers fun, stress-free, and creative experiential events. <br />We bring the celebration to your home, business, organization, or other venue.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {services.map((service, i) => <div key={i} data-index={i} className={`group relative transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="relative h-full overflow-hidden border border-secondary/30 bg-primary/20 backdrop-blur-xl shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-secondary/20">
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 border-2 border-secondary/20 rounded-xl blur-[1px]" />
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent translate-x-[-100%] group-hover:animate-[shimmer-x_2s_infinite]" />
                  <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent translate-x-[100%] group-hover:animate-[shimmer-x-reverse_2s_infinite]" />
                  <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-transparent via-secondary to-transparent translate-y-[-100%] group-hover:animate-[shimmer-y_2s_infinite]" />
                  <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-b from-transparent via-secondary to-transparent translate-y-[100%] group-hover:animate-[shimmer-y-reverse_2s_infinite]" />
                </div>

                <CardHeader className="relative z-10 pt-10 pb-4 flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-secondary/20 rounded-full blur-xl scale-0 transition-transform duration-700 group-hover:scale-150" />
                    <div className="relative flex items-center justify-center w-16 h-16 rounded-full border border-secondary/30 bg-secondary/10 text-secondary transition-all duration-500 group-hover:bg-secondary/20 group-hover:border-secondary">
                      <service.icon className="w-8 h-8" strokeWidth={1.5} />
                      <Sparkles className="absolute -top-2 -right-2 w-4 h-4 text-secondary opacity-0 transition-opacity duration-500 group-hover:opacity-100 animate-pulse" />
                    </div>
                  </div>
                  
                  <CardTitle className="text-2xl text-primary tracking-wide">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="relative z-10 text-center pb-10 px-8">
                  <CardDescription className="text-primary/80 leading-relaxed text-lg">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer-x {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes shimmer-x-reverse {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes shimmer-y {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes shimmer-y-reverse {
          0% { transform: translateY(100%); }
          100% { transform: translateY(-100%); }
        }
      `}</style>
    </section>;
}